/**
 * View-only strip for the "Calendar:" row on the Details step.
 *
 * Edit mode is handled upstream by a native `type: 'select'` field
 * with a condition on `isCalendarEditing`. This component covers the
 * read-only side only — it clicks over to edit mode, handles the
 * default-calendar seed, and opens the OAuth modal when the user
 * hasn't connected a calendar yet.
 */
import { useEffect, useRef, useState } from 'react';
import { OauthModal } from '@/components/ats/oauth';
import { CalendarField } from './CalendarField';
import type { SharedCal } from '../hooks/useScheduleCalendarData';

interface ScheduleCalendarRowFieldProps {
  summaryName: string;
  showYouSuffix: boolean;
  isLoading: boolean;
  hideEdit: boolean;
  disabledTooltip?: string;
  isCalConnected: boolean;
  /** Currently-seeded calendar_info — lets the view decide when to seed defaults. */
  calendarInfoId?: string;
  /** Shared-calendar list — drives the default-calendar seeding. */
  calendars: SharedCal[];
  defaultCalendar?: SharedCal;
  /** True when editing an existing interview — skip default seeding. */
  isEdit: boolean;
  /** Force a re-poll before the user opens the edit Select. */
  refetchUuid: () => void;
  /** Outlook-only organizer-hint tooltip text. Passed through to
   *  `CalendarField`; renders the info icon next to the calendar name. */
  outlookHint?: string;
  formMethods: {
    setValue: (_n: string, _v: unknown, _o?: { shouldDirty?: boolean }) => void;
  };
}

export const ScheduleCalendarRowField = ({
  summaryName,
  showYouSuffix,
  isLoading,
  hideEdit,
  disabledTooltip,
  isCalConnected,
  calendarInfoId,
  calendars,
  defaultCalendar,
  isEdit,
  refetchUuid,
  outlookHint,
  formMethods,
}: ScheduleCalendarRowFieldProps) => {
  const [oauthOpen, setOauthOpen] = useState(false);
  const seededRef = useRef(false);

  // Seed calendar_info with the default shared calendar once the
  // polling settles (create-only). Edit mode is left alone — the
  // hydrated scheduled_calendar is the source of truth there.
  useEffect(() => {
    if (isEdit) return;
    if (seededRef.current) return;
    if (!isCalConnected) return;
    if (calendars.length === 0) return;
    if (calendarInfoId) return;
    if (!defaultCalendar?.provider_calendar_id) return;

    seededRef.current = true;
    formMethods.setValue(
      'calendar_info',
      {
        provider_calendar_id: defaultCalendar.provider_calendar_id,
        is_primary: Boolean(defaultCalendar.is_primary),
      },
      { shouldDirty: false }
    );
    formMethods.setValue(
      'calendar_info_picker',
      defaultCalendar.provider_calendar_id,
      { shouldDirty: false }
    );
  }, [
    calendarInfoId,
    calendars,
    defaultCalendar,
    formMethods,
    isCalConnected,
    isEdit,
  ]);

  const handleStartEdit = () => {
    if (!isCalConnected) {
      setOauthOpen(true);
      return;
    }
    refetchUuid();
    // Keep the Select value in sync with the currently-stored id
    // before the user sees it.
    if (calendarInfoId) {
      formMethods.setValue('calendar_info_picker', calendarInfoId, {
        shouldDirty: false,
      });
    }
    formMethods.setValue('isCalendarEditing', 'edit');
  };

  return (
    <>
      <CalendarField
        calendarName={summaryName}
        suffixYou={showYouSuffix}
        loading={isLoading && !summaryName}
        hideEdit={hideEdit}
        disabledTooltip={disabledTooltip}
        outlookHint={outlookHint}
        onEdit={handleStartEdit}
      />
      <OauthModal
        isOpen={oauthOpen}
        onClose={() => setOauthOpen(false)}
        headerText="Connect your calendar"
        subHeaderText="Add new shared calendars or modify existing ones by connecting your calendar with Kula"
      />
    </>
  );
};
